import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Download, HelpCircle, FileText, Shield, RefreshCw, Inbox } from 'lucide-react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useAuditLogs, useAuditLogUsers, useExportAuditLogs } from '@/hooks/useAuditLogs';
import { AuditLogList } from '@/components/audit/AuditLogList';
import { AuditLogFilters } from '@/components/audit/AuditLogFilters';
import { AuditLogDetail } from '@/components/audit/AuditLogDetail';
import { AuditLogExport } from '@/components/audit/AuditLogExport';
import { AuditLog } from '@/types/auditLog';

export const AuditLogPage: React.FC = () => {
  const { user } = useAuth();
  const {
    logs,
    pagination,
    isLoading,
    error,
    filters,
    setFilters,
    clearFilters,
    setPage,
    refetch,
  } = useAuditLogs();

  const { data: users = [], isLoading: isLoadingUsers } = useAuditLogUsers();
  const { exportLogs, isExporting } = useExportAuditLogs();

  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  // Check permission - only CEO and MANAGER can view audit logs
  const canViewAuditLogs = user?.role === 'CEO' || user?.role === 'MANAGER';

  if (!canViewAuditLogs) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-500">
              You don't have permission to view audit logs.
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const handleLogClick = (log: AuditLog) => {
    setSelectedLog(log);
    setIsDetailOpen(true);
  };

  const handleExport = async (exportFilters: any, format: 'csv' | 'json') => {
    await exportLogs(exportFilters, format);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Audit Logs</h1>
            <p className="text-gray-600 mt-1">
              Track all system activities and changes
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setIsExportOpen(true)}
              aria-label="Export audit logs"
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
            <Button
              variant="ghost"
              onClick={() => setShowHelp(!showHelp)}
              aria-label={showHelp ? 'Close help panel' : 'Open help panel'}
              aria-expanded={showHelp}
            >
              <HelpCircle className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Help Panel */}
        {showHelp && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-blue-50 border border-blue-200 rounded-lg p-4"
          >
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-blue-900 mb-2">About Audit Logs</h3>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>Audit logs track all changes made to leads, tasks, and users</li>
                  <li>Use filters to narrow down results by date, user, or activity type</li>
                  <li>Click on any log entry to view detailed change information</li>
                  <li>Export logs for compliance reporting or analysis</li>
                  <li>Logs are retained according to your organization's data policy</li>
                </ul>
              </div>
              <button
                onClick={() => setShowHelp(false)}
                className="text-blue-500 hover:text-blue-700"
                aria-label="Close help panel"
              >
                Close
              </button>
            </div>
          </motion.div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <AuditLogFilters
            filters={filters}
            onFiltersChange={setFilters}
            onClear={clearFilters}
            users={users}
            isLoadingUsers={isLoadingUsers}
          />
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between" role="alert">
            <span className="text-red-700">Failed to load audit logs. Please try again.</span>
            <Button variant="outline" size="sm" onClick={() => refetch()} aria-label="Retry loading audit logs">
              <RefreshCw className="w-4 h-4 mr-1" /> Retry
            </Button>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && logs.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Inbox className="w-12 h-12 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">No audit logs found</h3>
            <p className="text-sm text-gray-500">Try adjusting your filters or check back later.</p>
          </div>
        )}

        {/* Logs List */}
        <AuditLogList
          logs={logs}
          isLoading={isLoading}
          onLogClick={handleLogClick}
          onLoadMore={() => setPage(pagination.page + 1)}
          hasMore={pagination.page < pagination.pages}
        />

        {/* Pagination */}
        {pagination.total > 0 && (
          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <div className="text-sm text-gray-500">
              Showing {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(pagination.page - 1)}
                disabled={pagination.page <= 1 || isLoading}
              >
                Previous
              </Button>

              {/* Page Numbers */}
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                  let pageNum: number;
                  if (pagination.pages <= 5) {
                    pageNum = i + 1;
                  } else if (pagination.page <= 3) {
                    pageNum = i + 1;
                  } else if (pagination.page >= pagination.pages - 2) {
                    pageNum = pagination.pages - 4 + i;
                  } else {
                    pageNum = pagination.page - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`w-8 h-8 text-sm rounded ${
                        pagination.page === pageNum
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-gray-600 hover:bg-gray-100'
                      }`}
                      disabled={isLoading}
                      aria-label={`Go to page ${pageNum}`}
                      aria-current={pagination.page === pageNum ? 'page' : undefined}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                {pagination.pages > 5 && pagination.page < pagination.pages - 2 && (
                  <>
                    <span className="text-gray-400">...</span>
                    <button
                      onClick={() => setPage(pagination.pages)}
                      className="w-8 h-8 text-sm rounded bg-white text-gray-600 hover:bg-gray-100"
                      disabled={isLoading}
                    >
                      {pagination.pages}
                    </button>
                  </>
                )}
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages || isLoading}
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {/* Detail Panel */}
        <AuditLogDetail
          log={selectedLog}
          isOpen={isDetailOpen}
          onClose={() => {
            setIsDetailOpen(false);
            setSelectedLog(null);
          }}
        />

        {/* Export Modal */}
        <AuditLogExport
          isOpen={isExportOpen}
          onClose={() => setIsExportOpen(false)}
          filters={filters}
          onExport={handleExport}
          isExporting={isExporting}
        />
      </div>
    </DashboardLayout>
  );
};

export default AuditLogPage;
